// =============================================================================
// demandesEngagement.js — le compteur des demandes d'engagement à valider
// (lot L11d-bis, demande de Pierre du 30/09/2026).
// -----------------------------------------------------------------------------
// Une demande d'engagement (collection `engagementRequests`) est « à valider »
// tant que son statut vaut `en_attente`. Le prédicat est UNIQUE : la page
// « Demandes d'engagement » (DemandesEngagementView.js) en tire sa liste, le
// hook ci-dessous son chiffre ; le nombre affiché dans les menus est donc
// exactement le nombre de demandes que la page liste.
//
// Petit fichier du bundle principal : Firestore n'y est importé qu'à la
// demande, et seulement pour un administrateur (session RÉELLE, jamais celle
// de la personne affichée en « Voir comme »).
// =============================================================================
import { useEffect, useState } from "react";

export const STATUT_EN_ATTENTE = "en_attente";

/** Vrai si la demande attend encore une décision (pur). */
export const estEnAttente = (d) => !!d && d.statut === STATUT_EN_ATTENTE;

/**
 * Projets ouverts d'une mutuelle restés SANS SUITE : ni statut posé, ni
 * demande en attente (pur). Un membre ne pose pas son statut, il le DEMANDE :
 * la relance « Engagements à renseigner » sort d'« À traiter » quand cette
 * liste est vide (EngagementMutuelleView, acte engagementsRenseignes).
 * `null` quand la mutuelle est inconnue : on ne sait pas, rien n'est soldé.
 * @param {object[]} projets  @param {string} mutId  id de la liste de la mutuelle
 * @param {object[]} demandes  demandes d'engagement de cette mutuelle
 */
export function engagementsSansSuite(projets, mutId, demandes) {
  if (!mutId) return null;
  const demandees = new Set((demandes || [])
    .filter((d) => estEnAttente(d) && (!d.mutuellleId || d.mutuellleId === mutId)).map((d) => d.projectId));
  return (projets || []).filter((p) => p && !p.clos && !p.archived
    && !(p.engagements || {})[mutId] && !demandees.has(p.id));
}

/**
 * Nombre de demandes d'engagement à valider, en direct.
 * Convention ternaire : `null` = on ne sait pas (droit absent, chargement,
 * erreur de lecture) ; un entier sinon. Ni `null` ni 0 ne font de pastille.
 * Égalité simple sur `statut` : index automatique, rien à déployer.
 * @param {boolean} actif  administrateur (session réelle) connecté
 */
export function useDemandesEngagementEnAttente(actif) {
  const [nombre, setNombre] = useState(null);
  useEffect(() => {
    if (!actif) { setNombre(null); return undefined; }
    let fini = false;
    let desabonner = null;
    (async () => {
      try {
        const { collection, query, where, onSnapshot } = await import("firebase/firestore");
        const { db } = await import("./firebase");
        if (fini) return;
        desabonner = onSnapshot(
          query(collection(db, "engagementRequests"), where("statut", "==", STATUT_EN_ATTENTE)),
          (snap) => { if (!fini) setNombre(snap.docs.filter((d) => estEnAttente(d.data())).length); },
          () => { if (!fini) setNombre(null); },
        );
      } catch (_) {
        if (!fini) setNombre(null);
      }
    })();
    return () => {
      fini = true;
      if (desabonner) desabonner();
    };
  }, [actif]);
  return actif ? nombre : null;
}
